<template>
  <ModalComponent
    ref="modal"
    size="xl"
    class="corporate-onboarding-modal"
    :dismissible
    :no-header="true"
    :aria-label="$t('billing.onboarding_label')"
    :no-footer="true"
    submit-button-text=""
    @close="$emit('dismiss')"
  >
    <template #body>
      <div class="corporate-onboarding" data-testid="corporate-onboarding">
        <OnboardingStepsComponent
          v-if="showSteps"
          :current="currentStep"
          class="mb-4"
        />

        <AgreementStepComponent
          v-if="view === 'agreement'"
          :loading="isSubmitting"
          @accept="handleAcceptAgreement"
        />

        <QualificationStepComponent
          v-else-if="view === 'qualification'"
          :initial-answers="savedAnswers"
          :loading="isSubmitting"
          @change="handleAnswerChange"
          @submit="handleQualification"
        />

        <PlanCheckoutStepComponent
          v-else-if="view === 'checkout' && status"
          :status
          :demo-url
          :loading="isSubmitting"
          @checkout="handleCheckout"
          @back="isEditingAnswers = true"
        />

        <DemoStepComponent
          v-else-if="view === 'demo'"
          :loading="isSubmitting"
          :requested="!!status?.demo_requested_at"
          @book="handleBookDemo"
          @back="isEditingAnswers = true"
        />

        <div
          v-else-if="confirmation"
          class="text-center py-5"
          :data-testid="`confirmation-${confirmation}`"
        >
          <div v-if="confirmation === 'confirming'" class="mb-3">
            <span
              class="spinner-border text-primary"
              role="status"
              aria-hidden="true"
            ></span>
          </div>
          <FontAwesomeIcon
            v-else
            :icon="confirmation === 'done' ? 'circle-check' : 'hourglass-half'"
            class="confirmation-icon mb-3"
          />
          <h2 class="font-serif">
            {{ $t(`billing.success.${confirmation}_title`) }}
          </h2>
          <p class="text-body-secondary">
            {{ $t(`billing.success.${confirmation}_body`) }}
          </p>
          <div
            v-if="confirmation !== 'confirming'"
            class="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-2"
          >
            <button
              v-if="canBookOnboardingCall"
              type="button"
              class="btn btn-primary btn-lg"
              :disabled="isSubmitting"
              data-testid="confirmation-book-call"
              @click="handleBookOnboardingCall"
            >
              <FontAwesomeIcon icon="calendar-check" class="me-2" />
              {{ $t("billing.onboarding_call.book") }}
            </button>
            <button
              type="button"
              class="btn btn-lg"
              :class="
                canBookOnboardingCall ? 'btn-outline-primary' : 'btn-primary'
              "
              data-testid="confirmation-continue"
              @click="close"
            >
              {{ $t("billing.success.continue") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
/**
 * The corporate onboarding that follows company signup, in one modal: four qualification questions (consumption
 * band, number of countries, reporting standards, regions), then the Platform Agreement for a company that has not
 * accepted it yet, then either the self-serve plan checkout
 * (Creem, for a single-country company under 10 GWh) or a demo booking. Mounted on the corporate and
 * trader dashboards, so existing clients get it too. Each answer is saved the moment it is given, so a
 * user who leaves halfway still leaves sales something to follow up on.
 *
 * The billing steps never run on a white-label portal (registry.ekoenergy.org sells no Soldera plan), where the
 * modal is only the agreement. The backend decides whether billing asks (`prompt_required`): it stays shut for paid-up companies,
 * test companies, and companies an admin marked onboarded with a "billing prompt disabled until" date. An enterprise
 * prospect that booked a demo stays on the demo step until sales closes the deal and an admin sets that date. It cannot be dismissed while a step is open, unless the parent passes `dismissible`. Creem sends the
 * user back with `?billing=success`; the subscription is activated by Creem's webhook, so the modal
 * polls the status until it reports paid.
 */
import { billingApi } from "@/billing/billingApi";
import type {
  BillingStatus,
  DemoOrganisation,
  QualificationAnswers,
  QualificationDraft,
} from "@/billing/billingTypes";
import AgreementStepComponent from "@/billing/AgreementStepComponent.vue";
import DemoStepComponent from "@/billing/DemoStepComponent.vue";
import OnboardingStepsComponent from "@/billing/OnboardingStepsComponent.vue";
import PlanCheckoutStepComponent from "@/billing/PlanCheckoutStepComponent.vue";
import QualificationStepComponent from "@/billing/QualificationStepComponent.vue";
import { isWhiteLabel } from "@/branding";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import type { Company } from "@/types";
import { useDocumentVisibility } from "@vueuse/core";
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

type Confirmation = "confirming" | "done" | "pending";

const CONFIRMATION_POLL_INTERVAL_MS = 2000;
const CONFIRMATION_POLL_ATTEMPTS = 10;
// The pending copy promises the plan switches on by itself, so a late webhook must still reach the dashboard.
const PENDING_POLL_INTERVAL_MS = 15000;
const PENDING_POLL_ATTEMPTS = 40;

const props = withDefaults(
  defineProps<{
    company: Company;
    /** Preselects the role on the demo booking page: the dashboard this modal is mounted on. */
    organisation: DemoOrganisation;
    /** The company has not accepted the Platform Agreement yet: it follows the qualification questions. */
    needsAgreement?: boolean;
    /** The user may close the modal while it still asks something; closing it emits `dismiss`. */
    dismissible?: boolean;
  }>(),
  { needsAgreement: false, dismissible: false }
);

const emit = defineEmits<{
  /** Creem's webhook confirmed the payment while the user watched. */
  paid: [];
  /** The customer opened the onboarding call booking page from the welcome screen. */
  "request:onboarding-call": [];
  /** The modal has nothing left to ask and closed. */
  close: [];
  /** The billing status could not be read, so the modal stayed shut. */
  "load-failed": [];
  /** The billing status was read and asks for nothing, so the modal stayed shut. */
  "nothing-to-ask": [];
  /** The user closed the modal while it still asked something (only when `dismissible`). */
  dismiss: [];
}>();

// Soldera sells its plans on its own portal only.
const isBillingPortal = !isWhiteLabel();

const status = ref<BillingStatus | null>(null);
const agreementAccepted = ref(false);
const isSubmitting = ref(false);
const isEditingAnswers = ref(false);
const confirmation = ref<Confirmation | null>(null);
let pollTimer: ReturnType<typeof setTimeout> | undefined;
// A read still out when the user leaves would otherwise start the next round of polling for a page they left.
let unmounted = false;
// Saves run one after another, so a quick change of mind can't land before the answer it replaces.
let pendingSave: Promise<void> = Promise.resolve();
// Counts every user action that may answer with a new status, so an older read never overwrites it.
let statusActions = 0;

const modal = useTemplateRef<InstanceType<typeof ModalComponent>>("modal");
const route = useRoute();
const { updateQuery } = useQueryManager();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const accountStore = useAccountStore();
const { t } = useI18n();
const visibility = useDocumentVisibility();

const savedAnswers = computed<QualificationDraft>(() => ({
  consumption_band: status.value?.consumption_band ?? null,
  country_scope: status.value?.country_scope ?? null,
  reporting_standards: status.value?.reporting_standards ?? [],
  reporting_standard_other: status.value?.reporting_standard_other ?? null,
  consumption_regions: status.value?.consumption_regions ?? [],
}));

const demoUrl = computed(() =>
  status.value ? withOrganisation(status.value.demo_url) : ""
);

const canBookOnboardingCall = computed(
  () => confirmation.value === "done" && !!status.value?.onboarding_call_due
);

const view = computed(() => {
  if (confirmation.value) return null;
  if (isEditingAnswers.value) return "qualification";
  // The agreement follows the questions: the customer first says what it needs, then signs up for it.
  if (status.value?.step === "qualification") return "qualification";
  if (props.needsAgreement && !agreementAccepted.value) return "agreement";
  return status.value?.step ?? null;
});

// The progress only makes sense when billing follows; the agreement on its own is a single step.
const showSteps = computed(
  () =>
    view.value !== null &&
    (view.value !== "agreement" || !!status.value?.prompt_required)
);

const currentStep = computed(() => {
  if (view.value === "agreement") return "agreement";
  if (view.value === "qualification") return "needs";
  return "plan";
});

onMounted(async () => {
  if (isBillingPortal && route.query.billing === "success") {
    try {
      await updateQuery({ billing: undefined });
    } catch (error) {
      // A cancelled navigation only leaves the flag in the URL; the confirmation must still run.
      globalErrorHandler(error);
    }
    confirmation.value = "confirming";
    setOpen(true);
    if (
      await pollUntilPaid(
        CONFIRMATION_POLL_ATTEMPTS,
        CONFIRMATION_POLL_INTERVAL_MS
      )
    )
      return;
    if (unmounted) return;
    confirmation.value = "pending";
    await pollUntilPaid(PENDING_POLL_ATTEMPTS, PENDING_POLL_INTERVAL_MS);
    return;
  }

  if (isBillingPortal) await loadStatus();
  if (props.needsAgreement || status.value?.prompt_required) setOpen(true);
  else if (status.value) emit("nothing-to-ask");
});

onUnmounted(() => {
  unmounted = true;
  clearTimeout(pollTimer);
});

// A prospect waiting on sales may keep the tab open for days: coming back to it picks up an unlock.
watch(visibility, async (current) => {
  if (current !== "visible" || view.value !== "demo" || isSubmitting.value)
    return;
  const actionsBefore = statusActions;
  try {
    const fresh = (await billingApi.getStatus(props.company.id!)).data;
    // Booking a demo or changing the answers while this read was out gave a newer status than it.
    if (statusActions !== actionsBefore) return;
    applyStatus(fresh);
  } catch (error) {
    globalErrorHandler(error);
  }
});

async function loadStatus() {
  try {
    status.value = (await billingApi.getStatus(props.company.id!)).data;
  } catch (error) {
    // Billing must never lock anyone out of their dashboard: on failure the modal just stays shut, and the payment wall,
    // which has nothing else to show, offers a retry.
    globalErrorHandler(error);
    emit("load-failed");
  }
}

async function pollUntilPaid(
  attemptsLeft: number,
  intervalMs: number
): Promise<boolean> {
  await loadStatus();
  if (unmounted) return false;
  if (isPaid(status.value)) {
    confirmation.value = "done";
    emit("paid");
    return true;
  }
  if (attemptsLeft <= 1) return false;
  await new Promise<void>((resolve) => {
    pollTimer = setTimeout(resolve, intervalMs);
  });
  return pollUntilPaid(attemptsLeft - 1, intervalMs);
}

// A suppressed prompt is not proof of payment (an admin may have switched it off): only Creem's webhook is.
function isPaid(current: BillingStatus | null): boolean {
  return !!current?.paid;
}

async function handleAcceptAgreement() {
  await submit(async () => {
    await accountStore.postAccountChange(
      `/api/company/${props.company.id}/confirm-registration`,
      { contract_version: "v5" }
    );
    agreementAccepted.value = true;
    globalSuccessHandler(
      t("common.success"),
      t("dashboard.buyer.terms_accepted")
    );
    if (!status.value?.prompt_required) close();
  });
}

function handleAnswerChange(draft: QualificationDraft) {
  pendingSave = pendingSave.then(async () => {
    try {
      await billingApi.saveAnswers(props.company.id!, draft);
    } catch (error) {
      globalErrorHandler(error);
    }
  });
}

async function handleQualification(answers: QualificationAnswers) {
  await submit(async () => {
    await pendingSave;
    isEditingAnswers.value = false;
    applyStatus(
      (await billingApi.submitQualification(props.company.id!, answers)).data
    );
  });
}

async function handleCheckout() {
  await submit(async () => {
    const { checkout_url } = (
      await billingApi.createCheckout(props.company.id!)
    ).data;
    window.location.assign(checkout_url);
  });
}

async function handleBookDemo() {
  // Opened before the request so the browser still treats it as a direct result of the click.
  window.open(demoUrl.value, "_blank", "noopener");
  await submit(async () => {
    // The modal stays: the dashboard opens once sales has agreed the deal.
    applyStatus((await billingApi.requestDemo(props.company.id!)).data);
  });
}

async function handleBookOnboardingCall() {
  // Opened before the request so the browser still treats it as a direct result of the click.
  window.open(
    withOrganisation(status.value!.onboarding_call_url),
    "_blank",
    "noopener"
  );
  await submit(async () => {
    status.value = (
      await billingApi.recordOnboardingCall(props.company.id!, "requested")
    ).data;
    emit("request:onboarding-call");
    close();
  });
}

async function submit(action: () => Promise<void>) {
  statusActions += 1;
  isSubmitting.value = true;
  try {
    await action();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
}

// Sales may unlock the dashboard while the modal is open, so any new status can leave nothing to ask but the agreement.
function applyStatus(fresh: BillingStatus) {
  status.value = fresh;
  const agreementPending = props.needsAgreement && !agreementAccepted.value;
  if (!fresh.prompt_required && !agreementPending) close();
}

function setOpen(open: boolean) {
  if (modal.value) modal.value.showModal = open;
}

function close() {
  setOpen(false);
  emit("close");
}

// Preselects the role on the booking page: the dashboard this modal is mounted on.
function withOrganisation(bookingUrl: string): string {
  const url = new URL(bookingUrl);
  url.searchParams.set("organisation", props.organisation);
  return url.toString();
}
</script>

<style scoped lang="scss">
// Wider than Bootstrap's xl, so the questions and the proof panel beside them both have room to breathe.
.corporate-onboarding-modal :deep(.modal-xl) {
  @media (min-width: 1200px) {
    --phoenix-modal-width: 1280px;
    max-width: min(1280px, calc(100vw - 3rem));
  }
}

.corporate-onboarding {
  padding: 2rem;

  @media (max-width: 575.98px) {
    padding: 1.25rem;
  }
}

.confirmation-icon {
  font-size: 3rem;
  color: var(--phoenix-success);
}
</style>
